/**
 * useQuoteContract.ts — Issue #327.
 *
 * Admin-side hooks for the formal quote / finance price-review / contract /
 * quote-change surface.
 *
 * 红线：
 *   - 仅使用 generated @jinzhuan/api-client；不手写 fetch。
 *   - 所有 mutation 必须使用 expectedVersion 走乐观锁。
 *   - mutation 返回值必须刷新 UI 状态；前端不允许伪造审批/合同状态。
 *   - 金额字段为 decimal 字符串；前端只展示不参与算术。
 *   - 列表视图只读取 summary 字段，详情接口才取整张报价 / 合同 / 变更。
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
} from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ============================================================================
// Type aliases
// ============================================================================

export type QuoteSummaryDto = ApiComponents['schemas']['QuoteSummaryDto'];
export type QuoteDetailDto = ApiComponents['schemas']['QuoteDetailDto'];
export type QuoteItemDto = ApiComponents['schemas']['QuoteItemDto'];
export type QuoteItemInputDto =
  ApiComponents['schemas']['QuoteItemInputDto'];
export type QuoteReviewDto = ApiComponents['schemas']['QuoteReviewDto'];
export type QuoteListResponseDto =
  ApiComponents['schemas']['QuoteListResponseDto'];
export type CreateQuoteDto = ApiComponents['schemas']['CreateQuoteDto'];
export type UpdateQuoteDto = ApiComponents['schemas']['UpdateQuoteDto'];
export type SubmitQuoteDto = ApiComponents['schemas']['SubmitQuoteDto'];
export type ApprovePriceReviewDto =
  ApiComponents['schemas']['ApprovePriceReviewDto'];
export type RejectPriceReviewDto =
  ApiComponents['schemas']['RejectPriceReviewDto'];
export type RequestChangesPriceReviewDto =
  ApiComponents['schemas']['RequestChangesPriceReviewDto'];
export type CancelQuoteDto = ApiComponents['schemas']['CancelQuoteDto'];

export type ContractSummaryDto =
  ApiComponents['schemas']['ContractSummaryDto'];
export type ContractDetailDto =
  ApiComponents['schemas']['ContractDetailDto'];
export type ContractListResponseDto =
  ApiComponents['schemas']['ContractListResponseDto'];
export type GenerateContractDto =
  ApiComponents['schemas']['GenerateContractDto'];
export type SignContractDto = ApiComponents['schemas']['SignContractDto'];
export type ActivateContractDto =
  ApiComponents['schemas']['ActivateContractDto'];
export type VoidContractDto = ApiComponents['schemas']['VoidContractDto'];

export type QuoteChangeRequestSummaryDto =
  ApiComponents['schemas']['QuoteChangeRequestSummaryDto'];
export type QuoteChangeRequestDetailDto =
  ApiComponents['schemas']['QuoteChangeRequestDetailDto'];
export type QuoteChangeRequestListResponseDto =
  ApiComponents['schemas']['QuoteChangeRequestListResponseDto'];
export type CreateQuoteChangeRequestDto =
  ApiComponents['schemas']['CreateQuoteChangeRequestDto'];
export type ApproveQuoteChangeRequestDto =
  ApiComponents['schemas']['ApproveQuoteChangeRequestDto'];
export type RejectQuoteChangeRequestDto =
  ApiComponents['schemas']['RejectQuoteChangeRequestDto'];
export type RequestChangesQuoteChangeRequestDto =
  ApiComponents['schemas']['RequestChangesQuoteChangeRequestDto'];
export type SubmitQuoteChangeRequestDto =
  ApiComponents['schemas']['SubmitQuoteChangeRequestDto'];
export type CancelQuoteChangeRequestDto =
  ApiComponents['schemas']['CancelQuoteChangeRequestDto'];
export type QuoteChangeReviewDto =
  ApiComponents['schemas']['QuoteChangeReviewDto'];

export type LatestQuoteContractSummaryDto =
  ApiComponents['schemas']['LatestQuoteContractSummaryDto'];
export type ProposalQuoteContractSummaryDto =
  ApiComponents['schemas']['ProposalQuoteContractSummaryDto'];

export type QuoteStatus = QuoteSummaryDto['status'];
export type ContractStatus = ContractSummaryDto['status'];
export type QuoteChangeStatus = QuoteChangeRequestSummaryDto['status'];

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: '草稿',
  submitted: '复核中',
  price_review_approved: '复核通过',
  contract_generated: '已生成合同',
  changes_requested: '待修改',
  cancelled: '已取消',
  superseded: '已被替换',
};

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  draft: '已生成',
  signed: '已签署',
  active: '生效中',
  change_pending: '变更中',
  voided: '已作废',
};

export const QUOTE_CHANGE_STATUS_LABELS: Record<QuoteChangeStatus, string> = {
  draft: '草稿',
  submitted: '审核中',
  approved: '已批准',
  rejected: '已拒绝',
  changes_requested: '待修改',
  cancelled: '已取消',
};

export const QUOTE_STATUSES: QuoteStatus[] = [
  'draft',
  'submitted',
  'price_review_approved',
  'contract_generated',
  'changes_requested',
  'cancelled',
  'superseded',
];

export const CONTRACT_STATUSES: ContractStatus[] = [
  'draft',
  'signed',
  'active',
  'change_pending',
  'voided',
];

export const QUOTE_CHANGE_STATUSES: QuoteChangeStatus[] = [
  'draft',
  'submitted',
  'approved',
  'rejected',
  'changes_requested',
  'cancelled',
];

// ============================================================================
// Error helpers
// ============================================================================

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

function toApiError(err: unknown, httpStatus?: number): ApiError {
  const status =
    httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401)
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED', status };
  if (status === 403)
    return { message: '当前角色没有报价/合同模块访问权限', code: 'FORBIDDEN', status };
  if (status === 404)
    return { message: '记录不存在或已被删除', code: 'NOT_FOUND', status };
  if (status === 409)
    return {
      message: '记录已被其他操作修改或状态不允许该操作，请刷新后重试',
      code: 'CONFLICT',
      status,
    };
  if (status === 400)
    return { message: '请求被拒绝，请检查必填字段、金额格式与状态前置', code: 'BAD_REQUEST', status };
  if (err instanceof Error) return { message: err.message, status };
  return { message: String(err), status };
}

// ============================================================================
// Quote list / detail
// ============================================================================

export interface QuoteListFilter {
  status?: QuoteStatus;
  customerId?: string;
  proposalId?: string;
  ownerUserId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function useQuoteList() {
  const [rows, setRows] = useState<QuoteSummaryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchQuotes = useCallback(async (filter?: QuoteListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.status) query.status = filter.status;
      if (filter?.customerId) query.customerId = filter.customerId;
      if (filter?.proposalId) query.proposalId = filter.proposalId;
      if (filter?.ownerUserId) query.ownerUserId = filter.ownerUserId;
      if (filter?.search) query.search = filter.search;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/quote-contract/quotes',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) {
        setRows([...data.rows]);
        setTotal(data.total);
        setPage(data.page);
        setLimit(data.limit);
      }
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { rows, total, page, limit, loading, error, fetchQuotes };
}

export function useQuoteDetail() {
  const [quote, setQuote] = useState<QuoteDetailDto | null>(null);
  const [reviews, setReviews] = useState<QuoteReviewDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchQuote = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/quote-contract/quotes/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setQuote(data ?? null);

      const { data: reviewsData, error: reviewsErr, response: reviewsRes } =
        await apiClient.GET('/api/v1/quote-contract/quotes/{id}/reviews', {
          params: { path: { id } },
        });
      if (reviewsErr) {
        setError(toApiError(reviewsErr, (reviewsRes as Response)?.status));
        return;
      }
      setReviews([...(reviewsData ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { quote, reviews, loading, error, fetchQuote, setQuote };
}

// ============================================================================
// Quote mutations
// ============================================================================

export function useCreateQuote() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createQuote = useCallback(
    async (dto: CreateQuoteDto): Promise<QuoteDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/quotes',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createQuote, loading, error };
}

export function useUpdateQuote() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const updateQuote = useCallback(
    async (
      id: string,
      dto: UpdateQuoteDto,
    ): Promise<QuoteDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT(
          '/api/v1/quote-contract/quotes/{id}',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateQuote, loading, error };
}

export function useQuoteTransitions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  function handleResult<T>(
    data: T | undefined,
    apiErr: unknown,
    response: unknown,
  ): T | null {
    if (apiErr) {
      setError(toApiError(apiErr, (response as Response | undefined)?.status));
      return null;
    }
    return data ?? null;
  }

  const submit = useCallback(
    async (id: string, dto: SubmitQuoteDto): Promise<QuoteDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/quotes/{id}/submit',
          { params: { path: { id } }, body: dto },
        );
        return handleResult(data, apiErr, response);
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const approve = useCallback(
    async (
      id: string,
      dto: ApprovePriceReviewDto,
    ): Promise<QuoteDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/quotes/{id}/approve',
          { params: { path: { id } }, body: dto },
        );
        return handleResult(data, apiErr, response);
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reject = useCallback(
    async (
      id: string,
      dto: RejectPriceReviewDto,
    ): Promise<QuoteDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/quotes/{id}/reject',
          { params: { path: { id } }, body: dto },
        );
        return handleResult(data, apiErr, response);
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const requestChanges = useCallback(
    async (
      id: string,
      dto: RequestChangesPriceReviewDto,
    ): Promise<QuoteDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/quotes/{id}/request-changes',
          { params: { path: { id } }, body: dto },
        );
        return handleResult(data, apiErr, response);
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancel = useCallback(
    async (
      id: string,
      dto: CancelQuoteDto,
    ): Promise<QuoteDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/quotes/{id}/cancel',
          { params: { path: { id } }, body: dto },
        );
        return handleResult(data, apiErr, response);
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, submit, approve, reject, requestChanges, cancel };
}

export function useGenerateContract() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const generate = useCallback(
    async (
      quoteId: string,
      dto: GenerateContractDto,
    ): Promise<ContractDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/quotes/{id}/generate-contract',
          { params: { path: { id: quoteId } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { generate, loading, error };
}

// ============================================================================
// Contract list / detail
// ============================================================================

export interface ContractListFilter {
  status?: ContractStatus;
  customerId?: string;
  proposalId?: string;
  page?: number;
  limit?: number;
}

export function useContractList() {
  const [rows, setRows] = useState<ContractSummaryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchContracts = useCallback(async (filter?: ContractListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.status) query.status = filter.status;
      if (filter?.customerId) query.customerId = filter.customerId;
      if (filter?.proposalId) query.proposalId = filter.proposalId;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/quote-contract/contracts',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) {
        setRows([...data.rows]);
        setTotal(data.total);
        setPage(data.page);
        setLimit(data.limit);
      }
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { rows, total, page, limit, loading, error, fetchContracts };
}

export function useContractDetail() {
  const [contract, setContract] = useState<ContractDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchContract = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/quote-contract/contracts/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setContract(data ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { contract, loading, error, fetchContract, setContract };
}

export function useContractTransitions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const sign = useCallback(
    async (
      id: string,
      dto: SignContractDto,
    ): Promise<ContractDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/contracts/{id}/sign',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const activate = useCallback(
    async (
      id: string,
      dto: ActivateContractDto,
    ): Promise<ContractDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/contracts/{id}/activate',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const voidContract = useCallback(
    async (
      id: string,
      dto: VoidContractDto,
    ): Promise<ContractDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/contracts/{id}/void',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, sign, activate, voidContract };
}

// ============================================================================
// Quote-change request list / detail / mutations
// ============================================================================

export interface QuoteChangeListFilter {
  status?: QuoteChangeStatus;
  contractId?: string;
  page?: number;
  limit?: number;
}

export function useQuoteChangeList() {
  const [rows, setRows] = useState<QuoteChangeRequestSummaryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchChanges = useCallback(async (filter?: QuoteChangeListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.status) query.status = filter.status;
      if (filter?.contractId) query.contractId = filter.contractId;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/quote-contract/change-requests',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) {
        setRows([...data.rows]);
        setTotal(data.total);
        setPage(data.page);
        setLimit(data.limit);
      }
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { rows, total, page, limit, loading, error, fetchChanges };
}

export function useQuoteChangeDetail() {
  const [change, setChange] = useState<QuoteChangeRequestDetailDto | null>(
    null,
  );
  const [reviews, setReviews] = useState<QuoteChangeReviewDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchChange = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/quote-contract/change-requests/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setChange(data ?? null);

      const { data: reviewsData, error: reviewsErr, response: reviewsRes } =
        await apiClient.GET(
          '/api/v1/quote-contract/change-requests/{id}/reviews',
          { params: { path: { id } } },
        );
      if (reviewsErr) {
        setError(toApiError(reviewsErr, (reviewsRes as Response)?.status));
        return;
      }
      setReviews([...(reviewsData ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { change, reviews, loading, error, fetchChange, setChange };
}

export function useQuoteChangeMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(
    async (
      dto: CreateQuoteChangeRequestDto,
    ): Promise<QuoteChangeRequestDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/change-requests',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  function handleResult<T>(
    data: T | undefined,
    apiErr: unknown,
    response: unknown,
  ): T | null {
    if (apiErr) {
      setError(toApiError(apiErr, (response as Response | undefined)?.status));
      return null;
    }
    return data ?? null;
  }

  const submit = useCallback(
    async (
      id: string,
      dto: SubmitQuoteChangeRequestDto,
    ): Promise<QuoteChangeRequestDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/change-requests/{id}/submit',
          { params: { path: { id } }, body: dto },
        );
        return handleResult(data, apiErr, response);
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const approve = useCallback(
    async (
      id: string,
      dto: ApproveQuoteChangeRequestDto,
    ): Promise<QuoteChangeRequestDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/change-requests/{id}/approve',
          { params: { path: { id } }, body: dto },
        );
        return handleResult(data, apiErr, response);
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reject = useCallback(
    async (
      id: string,
      dto: RejectQuoteChangeRequestDto,
    ): Promise<QuoteChangeRequestDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/change-requests/{id}/reject',
          { params: { path: { id } }, body: dto },
        );
        return handleResult(data, apiErr, response);
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const requestChanges = useCallback(
    async (
      id: string,
      dto: RequestChangesQuoteChangeRequestDto,
    ): Promise<QuoteChangeRequestDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/change-requests/{id}/request-changes',
          { params: { path: { id } }, body: dto },
        );
        return handleResult(data, apiErr, response);
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancel = useCallback(
    async (
      id: string,
      dto: CancelQuoteChangeRequestDto,
    ): Promise<QuoteChangeRequestDetailDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/quote-contract/change-requests/{id}/cancel',
          { params: { path: { id } }, body: dto },
        );
        return handleResult(data, apiErr, response);
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, create, submit, approve, reject, requestChanges, cancel };
}

// ============================================================================
// Customer / Proposal summary
// ============================================================================

export function useCustomerQuoteContractSummary() {
  const [summary, setSummary] =
    useState<LatestQuoteContractSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchSummary = useCallback(async (customerId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/quote-contract/customers/{customerId}/latest-summary',
        { params: { path: { customerId } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setSummary(data ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { summary, loading, error, fetchSummary, setSummary };
}

export function useProposalQuoteContractSummary() {
  const [summary, setSummary] =
    useState<ProposalQuoteContractSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchSummary = useCallback(async (proposalId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/quote-contract/proposals/{proposalId}/summary',
        { params: { path: { proposalId } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setSummary(data ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { summary, loading, error, fetchSummary, setSummary };
}
